<template>
  <v-col class="tw:p-0">
    <v-row no-gutters class="pb-5">
      <card-title-row translation-path="admin.basemaps.title" :has-icon-column="false" disable-title-buttons-gap>
        <template #buttons>
          <base-button
            icon="mdi-plus"
            :tooltip="$i18n.t('admin.basemaps.addBasemap')"
            @click="openBasemapConfigDialog()"
          />
        </template>
      </card-title-row>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12" v-if="fetched">
        <draggable-list
          fill-width
          allow-elements-on-main-level
          has-drag-handler
          disable-group-adding
          :value.sync="items"
          item-title-key="name"
          class="tw:[&_li]:last-of-type:border-b-0"
          @elementUpdated="elementUpdated"
        >
          <template #[`item.postappend-title`]="{ item, visible }">
            <div class="flexrow-0 tw:items-center">
              <base-button
                :icon="visible ? 'mdi-eye' : 'mdi-eye-off'"
                type="text"
                :color="visible ? 'primary' : undefined"
                :disabled="visible && isToggleVisibilityDisabled"
                :tooltip="$i18n.t(visible ? 'tooltips.turnOffLayerVisibility' : 'tooltips.turnOnLayerVisibility')"
                @click.stop="toggleVisibility(item)"
              />
              <dots-menu
                :items="[{ name: 'edit' }, ...(item.role !== 'basemap' ? [{ name: 'delete' }] : [])]"
                @delete="openDeleteDialog(item.id)"
                @edit="openBasemapConfigDialog(item.id)"
              />
            </div>
          </template>
        </draggable-list>
        <draggable-list
          fill-width
          allow-elements-on-main-level
          disable-draggable
          disable-group-adding
          :value="[
            {
              name: $i18n.t('admin.basemaps.empty'),
              visible: blankBasemapVisible,
            },
          ]"
          item-title-key="name"
        >
          <template #[`item.postappend-title`]="{ visible }">
            <base-button
              :icon="visible ? 'mdi-eye' : 'mdi-eye-off'"
              type="text"
              :color="visible ? 'primary' : undefined"
              :disabled="visible && isToggleVisibilityDisabled"
              :tooltip="$i18n.t(visible ? 'tooltips.turnOffLayerVisibility' : 'tooltips.turnOnLayerVisibility')"
              @click.stop="toggleBlankBasemapVisibility()"
            />
          </template>
        </draggable-list>
      </v-col>
      <main-loader v-else />
    </v-row>
    <manage-basemap-dialog
      v-if="isManageBasemapVisible"
      :is-visible.sync="isManageBasemapVisible"
      :id="manageBasemapId"
    />
    <main-dialog
      v-if="isDeleteDialogVisible"
      :is-visible.sync="isDeleteDialogVisible"
      :loading="isDeleteDialogLoading"
      :title="$i18n.t('admin.basemaps.deleteTitle')"
      :body="$i18n.t('admin.basemaps.deleteBody')"
      @agree="handleDelete"
      agree-text="dialog.agreeDelete"
    />
  </v-col>
</template>

<script>
import DraggableList from '@/components/DraggableList';
import MainLoader from '@/components/MainLoader';
import CardTitleRow from '@/components/CardTitleRow';
import ManageBasemapDialog from '@/components/ManageBasemapDialog';
import DotsMenu from '@/components/DotsMenu';
import MainDialog from '@/components/MainDialog';

import { get, call } from 'vuex-pathify';
import i18n from '@/plugins/i18n';

export default {
  name: 'BasemapsSettings',
  components: {
    DraggableList,
    MainLoader,
    CardTitleRow,
    DotsMenu,
    MainDialog,
    ManageBasemapDialog,
  },
  data: () => ({
    fetched: false,
    isManageBasemapVisible: false,
    manageBasemapId: null,

    isDeleteDialogVisible: false,
    isDeleteDialogLoading: false,
    layerIdToDelete: null,
  }),
  computed: {
    basemapSchema: get('layers/layersSchemaByScope@basemap@[0]'),
    basemapLayers: get('layers/basemapLayers'),
    blankBasemapVisible: get('admin/settingsValues@basemap_blank_visible'),
    isToggleVisibilityDisabled() {
      const visibleBasemaps = this.items.filter(layer => !!layer.visible);
      if (this.blankBasemapVisible) return visibleBasemaps.length <= 0;
      else return visibleBasemaps.length <= 1;
    },
    items() {
      return [
        ...(this.basemapSchema?.layers ?? []).map(layer => {
          const metadata = this.basemapLayers[layer.id];
          return {
            id: layer.id,
            name: layer.name,
            visible: layer.visible,
            role: metadata?.role,
          };
        }),
      ];
    },
  },
  methods: {
    getLayersSchemaByScope: call('layers/getLayersSchemaByScope'),
    setLayerVisibility: call('layers/setLayerVisibility'),
    attachMultipleLayerGroup: call('layers/attachMultipleLayerGroup'),
    editSingleSetting: call('admin/editSingleSetting'),
    deleteLayer: call('layers/deleteLayer'),
    openDeleteDialog(id) {
      this.layerIdToDelete = id;
      this.isDeleteDialogVisible = true;
    },
    async handleDelete() {
      this.isDeleteDialogLoading = true;
      try {
        await this.deleteLayer({
          layer_id: this.layerIdToDelete,
          layer_type: 'service_layers',
          scope: 'basemap',
        });
        this.isDeleteDialogVisible = false;
        this.layerIdToDelete = null;
      } catch (error) {
        console.error(error);
      } finally {
        this.isDeleteDialogLoading = false;
      }
    },
    openBasemapConfigDialog(id) {
      this.isManageBasemapVisible = true;
      this.manageBasemapId = id ?? null;
    },
    async elementUpdated(e) {
      const newOrder = e.newIndex;
      const item = e.item._underlying_vm_;
      await this.attachMultipleLayerGroup({
        body: {
          layers: [{ cache: false, id: item.id }],
          group_id: this.basemapSchema.id,
          order_in_group: +newOrder + 1,
        },
        scope: 'basemap',
      });
    },
    async toggleBlankBasemapVisibility() {
      await this.editSingleSetting({
        key: 'basemap_blank_visible',
        body: {
          value: !this.blankBasemapVisible,
        },
      });
    },
    async toggleVisibility(item) {
      await this.setLayerVisibility({
        layer_id: item.id,
        group_id: this.basemapSchema.id,
        schema_scope: 'basemap',
        params: {
          visible: !item.visible,
        },
      });
    },
    async parseSchema() {
      try {
        await this.getLayersSchemaByScope({ scope: 'basemap' });
      } catch (error) {
        console.error(error);
      } finally {
        this.fetched = true;
      }
    },
  },
  mounted() {
    this.parseSchema();
  },
};
</script>
